import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from './supabase'
import { narrowCountry } from './country'

/**
 * Shape of an `accounts` table row. Re-exports the Supabase-generated row type
 * so schema changes propagate automatically. `plan_limits` is typed as `Json`
 * (Supabase's nullable JSONB union); consumers that need a specific shape
 * should narrow via a runtime type-guard before access.
 * @typedef {import('../../types/supabase').Database['public']['Tables']['accounts']['Row']} AccountRow
 * @typedef {import('./planGate').PlanTier} PlanTier
 */

/**
 * Narrow an untrusted plan string (e.g. from DB) to PlanTier. Duplicated from
 * planGate.jsx's `asPlanTier` to avoid a circular import (planGate imports
 * usePlanLimits from here). Keep the two in sync.
 * @param {string | null | undefined} value
 * @returns {PlanTier}
 */
function narrowPlan(value) {
  if (value === 'starter' || value === 'pro' || value === 'enterprise') return value
  return 'free'
}

/**
 * @typedef {{ pack_key: string, status: string, valid_until: string | null }} EntitlementRow
 */

/**
 * @typedef {Object} AccountContextValue
 * @property {AccountRow | null} account
 * @property {string | undefined} accountId
 * @property {PlanTier} plan
 * @property {'ca' | 'us'} country
 * @property {string[]} entitlements
 * @property {boolean} loading
 * @property {() => Promise<void>} reload
 */

/**
 * @typedef {Object} PlanLimits
 * @property {number} maxCrewMembers
 * @property {number} maxSites
 * @property {number} maxClients
 * @property {number} maxStorageMb
 * @property {number} maxVerticals
 * @property {boolean} cronEmailsEnabled
 */

/** @type {React.Context<AccountContextValue>} */
const AccountContext = createContext(/** @type {AccountContextValue} */ ({
  account: null, accountId: undefined, plan: 'free', country: 'ca', entitlements: [], loading: true,
  reload: async () => {},
}))

/** @type {Record<string, PlanLimits>} */
const PLAN_LIMITS = {
  free:       { maxCrewMembers: 3, maxSites: 3, maxClients: 3, maxStorageMb: 100, maxVerticals: 1, cronEmailsEnabled: false },
  starter:    { maxCrewMembers: 10, maxSites: 20, maxClients: 20, maxStorageMb: 1024, maxVerticals: 3, cronEmailsEnabled: true },
  pro:        { maxCrewMembers: 50, maxSites: 100, maxClients: 100, maxStorageMb: 10240, maxVerticals: Infinity, cronEmailsEnabled: true },
  enterprise: { maxCrewMembers: Infinity, maxSites: Infinity, maxClients: Infinity, maxStorageMb: Infinity, maxVerticals: Infinity, cronEmailsEnabled: true },
}

/**
 * @param {{ children: React.ReactNode }} props
 */
export function AccountProvider({ children }) {
  const [account, setAccount] = useState(/** @type {AccountRow | null} */ (null))
  const [entitlements, setEntitlements] = useState(/** @type {string[]} */ ([]))
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setAccount(null); setLoading(false); return }

    // Try direct lookup (account owner)
    let { data: acc } = await supabase
      .from('accounts')
      .select('*, account_entitlements(pack_key, status, valid_until)')
      .eq('user_id', user.id)
      .maybeSingle()

    // Fallback: crew member — resolve via profiles.account_id
    if (!acc) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('account_id')
        .eq('id', user.id)
        .single()
      if (profile?.account_id) {
        const { data: memberAcc } = await supabase
          .from('accounts')
          .select('*, account_entitlements(pack_key, status, valid_until)')
          .eq('id', profile.account_id)
          .single()
        acc = memberAcc
      }
    }

    const now = new Date().toISOString()
    const activeEntitlements = (/** @type {EntitlementRow[] | undefined} */ (/** @type {any} */ (acc)?.account_entitlements) ?? [])
      .filter((e) => e.status === 'active' && (e.valid_until === null || e.valid_until > now))
      .map((e) => e.pack_key)

    setEntitlements(activeEntitlements)
    setAccount(acc)
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') load()
      if (event === 'SIGNED_OUT') { setAccount(null); setEntitlements([]); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [load])

  const value = useMemo(() => /** @type {AccountContextValue} */ ({
    account,
    accountId: account?.id,
    plan: narrowPlan(account?.plan),
    country: narrowCountry(account?.country),
    entitlements,
    loading,
    reload: load,
  }), [account, entitlements, loading, load])

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
}

/**
 * @returns {AccountContextValue}
 */
export function useAccount() {
  return useContext(AccountContext)
}

/**
 * @returns {{ plan: PlanTier, limits: PlanLimits, isPaid: boolean, cronEmailsEnabled: boolean, canAddCrewMember: (n: number) => boolean, canAddContractor: (n: number) => boolean, canAddSite: (n: number) => boolean, canAddClient: (n: number) => boolean, canAddVertical: (n: number) => boolean }}
 */
export function usePlanLimits() {
  const { account } = useAccount()
  const plan = narrowPlan(account?.plan)
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS['free']

  return {
    plan,
    limits,
    isPaid: plan !== 'free',
    /** @param {number} currentCount */ canAddCrewMember: (currentCount) => currentCount < (limits.maxCrewMembers ?? Infinity),
    /** @param {number} currentCount */ canAddContractor: (currentCount) => currentCount < (limits.maxCrewMembers ?? Infinity),
    /** @param {number} currentCount */ canAddSite: (currentCount) => currentCount < (limits.maxSites ?? Infinity),
    /** @param {number} currentCount */ canAddClient: (currentCount) => currentCount < (limits.maxClients ?? Infinity),
    /** @param {number} currentCount */ canAddVertical: (currentCount) => currentCount < (limits.maxVerticals ?? 1),
    cronEmailsEnabled: limits.cronEmailsEnabled ?? false,
  }
}

/**
 * @returns {{ entitlements: string[], hasPack: (packKey: string) => boolean }}
 */
export function useEntitlements() {
  const { entitlements } = useAccount()
  return {
    entitlements,
    hasPack: (packKey) => entitlements.includes(packKey),
  }
}
